import React from 'react';
import {
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Input,
  Badge,
  Table,
  Spinner,
  Pagination,
  PaginationItem,
  PaginationLink,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from 'reactstrap';

import {
  FaExclamationTriangle,
  FaExclamationCircle,
  FaInfoCircle,
  FaSearch,
  FaEllipsisV,
  FaEye,
} from 'react-icons/fa';

const ITEMS_PER_PAGE = 5;

const getLevelColor = (level) => {
  switch (level) {
    case 'Critica':
      return '#dc2626';
    case 'Advertencia':
      return '#f59e0b';
    case 'Informativa':
      return '#2563eb';
    default:
      return '#64748b';
  }
};

const getStatusColor = (status) => {
  switch (status?.trim().toLowerCase()) {
    case 'nueva':
      return '#dc2626';
    case 'en seguimiento':   
      return '#f59e0b';
    case 'resuelta':
      return '#16a34a';
    default:
      return '#64748b';
  }
};

const getLevelIcon = (level) => {
  const iconStyle = {
    width: '28px',
    height: '28px',
    minWidth: '28px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: '10px',
  };

  switch (level) {
    case 'Critica':
      return (
        <div
          style={{
            ...iconStyle,
            backgroundColor: '#fff0f1',
            color: '#dc2626',
          }}
        >
          <FaExclamationCircle size={13} />
        </div>
      );

    case 'Advertencia':
      return (
        <div
          style={{
            ...iconStyle,
            backgroundColor: '#fff8eb',
            color: '#f59e0b',
          }}
        >
          <FaExclamationTriangle size={13} />
        </div>
      );

    case 'Informativa':
      return (
        <div
          style={{
            ...iconStyle,
            backgroundColor: '#eef6ff',
            color: '#2563eb',
          }}
        >
          <FaInfoCircle size={13} />
        </div>
      );

    default:
      return null;
  }
};

const PaginationRange = ({
  currentPage,
  totalPages,
  setCurrentPage,
}) => {
  const getPageRange = () => {
    const delta = 2;
    const range = [];
    const rangeWithDots = [];
    let l;

    for (let i = 1; i <= totalPages; i++) {
      if (
        i === 1 ||
        i === totalPages ||
        (i >= currentPage - delta &&
          i <= currentPage + delta)
      ) {
        range.push(i);
      }
    }

    range.forEach((i) => {
      if (l) {
        if (i - l === 2) {
          rangeWithDots.push(l + 1);
        } else if (i - l !== 1) {
          rangeWithDots.push('...');
        }
      }

      rangeWithDots.push(i);
      l = i;
    });

    return rangeWithDots;
  };

  if (totalPages <= 1) return null;

  const pages = getPageRange();

  return (
    <Pagination
      size="sm"
      className="mb-0"
      style={{
        gap: '4px',
      }}
    >
      <PaginationItem disabled={currentPage === 1}>
        <PaginationLink
          previous
          onClick={() =>
            setCurrentPage((p) => Math.max(p - 1, 1))
          }
          style={{
            width: '34px',
            height: '34px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '5px',
            border: '1px solid #e5e9ef',
            color: '#64748b',
            backgroundColor: '#fff',
          }}
        />
      </PaginationItem>

      {pages.map((page, index) =>
        page === '...' ? (
          <PaginationItem
            key={`dots-${index}`}
            disabled
          >
            <PaginationLink
              style={{
                border: 'none',
                backgroundColor: 'transparent',
              }}
            >
              ...
            </PaginationLink>
          </PaginationItem>
        ) : (
          <PaginationItem
            key={page}
            active={currentPage === page}
          >
            <PaginationLink
              onClick={() => setCurrentPage(page)}
              style={{
                width: '34px',
                height: '34px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '5px',
                border:
                  currentPage === page
                    ? '1px solid #172337'
                    : '1px solid #e5e9ef',
                backgroundColor:
                  currentPage === page
                    ? '#172337'
                    : '#ffffff',
                color:
                  currentPage === page
                    ? '#ffffff'
                    : '#334155',
                fontSize: '12px',
                fontWeight: '600',
              }}
            >
              {page}
            </PaginationLink>
          </PaginationItem>
        )
      )}

      <PaginationItem disabled={currentPage >= totalPages}>
        <PaginationLink
          next
          onClick={() =>
            setCurrentPage((p) =>
              Math.min(p + 1, totalPages)
            )
          }
          style={{
            width: '34px',
            height: '34px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '5px',
            border: '1px solid #e5e9ef',
            color: '#64748b',
            backgroundColor: '#fff',
          }}
        />
      </PaginationItem>
    </Pagination>
  );
};

const AlertTable = ({
  alerts,
  pagination,
  isLoading,
  filterLevel,
  filterStatus,
  searchTerm,
  onFilterLevelChange,
  onFilterStatusChange,
  onSearchChange,
  onSearchEnter,
  currentPage,
  setCurrentPage,
  updateStatus,
  isUpdatingStatus,
  onViewAlert,
}) => {
  const totalPages = pagination.totalPages || 1;

  return (
    <Card
      style={{
        backgroundColor: '#ffffff',
        border: '1px solid #e7ebf0',
        borderRadius: '8px',
        boxShadow: '0 1px 4px rgba(0, 0, 0, 0.03)',
        overflow: 'hidden',
      }}
    >
      <CardBody
        style={{
          padding: '18px 16px 14px',
        }}
      >
        <div
          style={{
            fontSize: '15px',
            fontWeight: '700',
            color: '#1f2937',
            marginBottom: '14px',
          }}
        >
          Alertas Activas
        </div>

        <Row
          className="g-2 mb-3"
          style={{
            alignItems: 'center',
          }}
        >
          <Col md="3" xs="6">
            <Input
              type="select"
              value={filterLevel}
              onChange={onFilterLevelChange}
              style={{
                height: '36px',
                border: '1px solid #e2e7ed',
                borderRadius: '6px',
                backgroundColor: '#ffffff',
                color: '#475569',
                fontSize: '11px',
                fontWeight: '500',
                paddingLeft: '12px',
                boxShadow: 'none',
              }}
            >
              <option>Todos los Niveles</option>
              <option>Critica</option>
              <option>Advertencia</option>
              <option>Informativa</option>
            </Input>
          </Col>

          <Col md="3" xs="6">
            <Input
              type="select"
              value={filterStatus}
              onChange={onFilterStatusChange}
              style={{
                height: '36px',
                border: '1px solid #e2e7ed',
                borderRadius: '6px',
                backgroundColor: '#ffffff',
                color: '#475569',
                fontSize: '11px',
                fontWeight: '500',
                paddingLeft: '12px',
                boxShadow: 'none',
              }}
            >
              <option>Todos los Estados</option>
              <option>Nueva</option>
              <option>En seguimiento</option>
              <option>Resuelta</option>
            </Input>
          </Col>

          <Col md="6" xs="12">
            <div
              style={{
                position: 'relative',
              }}
            >
              <FaSearch
                style={{
                  position: 'absolute',
                  left: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#64748b',
                  fontSize: '12px',
                  zIndex: 2,
                }}
              />

              <Input
                type="text"
                placeholder="Buscar alerta o distribuidor..."
                value={searchTerm}
                onChange={onSearchChange}
                onKeyDown={onSearchEnter}
                style={{
                  height: '36px',
                  paddingLeft: '34px',
                  paddingRight: '38px',
                  border: '1px solid #e2e7ed',
                  borderRadius: '6px',
                  color: '#475569',
                  fontSize: '11px',
                  boxShadow: 'none',
                }}
              />

              <div
                style={{
                  position: 'absolute',
                  right: '0',
                  top: '0',
                  height: '36px',
                  width: '36px',
                  borderLeft: '1px solid #e2e7ed',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#334155',
                }}
              >
                <FaSearch size={11} />
              </div>
            </div>
          </Col>
        </Row>

        <div
          className="table-responsive"
          style={{
            borderTop: '1px solid #eef1f4',
          }}
        >
          <Table
            hover
            className="mb-0"
            style={{
              minWidth: '750px',
            }}
          >
            <thead>
              <tr
                style={{
                  backgroundColor: '#fafbfc',
                }}
              >
                <th
                  style={{
                    color: '#344054',
                    fontSize: '11px',
                    fontWeight: '700',
                    padding: '13px 10px',
                    borderBottom: '1px solid #e8ecf1',
                    width: '27%',
                  }}
                >
                  Alerta
                </th>

                <th
                  style={{
                    color: '#344054',
                    fontSize: '11px',
                    fontWeight: '700',
                    padding: '13px 10px',
                    borderBottom: '1px solid #e8ecf1',
                    width: '12%',
                  }}
                >
                  Nivel
                </th>

                <th
                  style={{
                    color: '#344054',
                    fontSize: '11px',
                    fontWeight: '700',
                    padding: '13px 10px',
                    borderBottom: '1px solid #e8ecf1',
                    width: '21%',
                  }}
                >
                  Distribuidor / Usuario
                </th>

                <th
                  style={{
                    color: '#344054',
                    fontSize: '11px',
                    fontWeight: '700',
                    padding: '13px 10px',
                    borderBottom: '1px solid #e8ecf1',
                    width: '15%',
                  }}
                >
                  Fecha
                </th>

                <th
                  style={{
                    color: '#344054',
                    fontSize: '11px',
                    fontWeight: '700',
                    padding: '13px 10px',
                    borderBottom: '1px solid #e8ecf1',
                    width: '15%',
                  }}
                >
                  Estado
                </th>

                <th
                  style={{
                    color: '#344054',
                    fontSize: '11px',
                    fontWeight: '700',
                    padding: '13px 10px',
                    borderBottom: '1px solid #e8ecf1',
                    textAlign: 'center',
                    width: '10%',
                  }}
                >
                  Acciones
                </th>
              </tr>
            </thead>

            <tbody>
              {isLoading && (
                <tr>
                  <td
                    colSpan="6"
                    style={{
                      textAlign: 'center',
                      padding: '35px',
                      borderBottom: '1px solid #eef1f4',
                    }}
                  >
                    <Spinner size="sm" color="primary" />
                  </td>
                </tr>
              )}

              {!isLoading &&
                alerts.map((alert) => (
                  <tr key={alert.id}>
                    <td
                      style={{
                        padding: '12px 10px',
                        borderBottom: '1px solid #eef1f4',
                        verticalAlign: 'middle',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        {getLevelIcon(alert.level)}

                        <span
                          style={{
                            color: '#273142',
                            fontSize: '11px',
                            fontWeight: '600',
                            lineHeight: '1.4',
                          }}
                        >
                          {alert.title}
                        </span>
                      </div>
                    </td>

                    <td
                      style={{
                        padding: '12px 10px',
                        borderBottom: '1px solid #eef1f4',
                        verticalAlign: 'middle',
                      }}
                    >
                      <Badge
                        pill
                        style={{
                          fontSize: '10px',
                          fontWeight: '600',
                          padding: '5px 10px',
                          border: 'none',
                          backgroundColor: getLevelColor(
                            alert.level
                          ),
                          color: '#ffffff',
                        }}
                      >
                        {alert.level}
                      </Badge>
                    </td>

                    <td
                      style={{
                        padding: '12px 10px',
                        borderBottom: '1px solid #eef1f4',
                        verticalAlign: 'middle',
                      }}
                    >
                      <div>
                        <div
                          style={{
                            color: '#273142',
                            fontSize: '11px',
                            fontWeight: '600',
                            lineHeight: '1.4',
                          }}
                        >
                          {alert.user}
                        </div>

                        {alert.code && (
                          <div
                            style={{
                              color: '#64748b',
                              fontSize: '10px',
                              marginTop: '2px',
                            }}
                          >
                            {alert.code}
                          </div>
                        )}
                      </div>
                    </td>

                    <td
                      style={{
                        padding: '12px 10px',
                        borderBottom: '1px solid #eef1f4',
                        verticalAlign: 'middle',
                      }}
                    >
                      <div>
                        <div
                          style={{
                            color: '#273142',
                            fontSize: '10px',
                            lineHeight: '1.5',
                          }}
                        >
                          {alert.date}
                        </div>

                        <div
                          style={{
                            color: '#64748b',
                            fontSize: '10px',
                            lineHeight: '1.5',
                          }}
                        >
                          {alert.time}
                        </div>
                      </div>
                    </td>

                    <td
                      style={{
                        padding: '12px 10px',
                        borderBottom: '1px solid #eef1f4',
                        verticalAlign: 'middle',
                      }}
                    >
                      <Badge
                        pill
                        style={{
                          fontSize: '10px',
                          fontWeight: '600',
                          padding: '5px 10px',
                          border: 'none',
                          backgroundColor: getStatusColor(
                            alert.status
                          ),
                          color: '#ffffff',
                        }}
                      >
                        {alert.status}
                      </Badge>
                    </td>

                    <td
                      style={{
                        padding: '12px 10px',
                        borderBottom: '1px solid #eef1f4',
                        verticalAlign: 'middle',
                        textAlign: 'center',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Button
                          color="link"
                          size="sm"
                          onClick={() =>
                            onViewAlert(alert)
                          }
                          title="Ver detalle"
                          style={{
                            width: '30px',
                            height: '30px',
                            padding: '0',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#64748b',
                            border: '1px solid #e2e7ed',
                            borderRadius: '5px',
                            textDecoration: 'none',
                            marginRight: '6px',
                          }}
                        >
                          <FaEye size={12} />
                        </Button>

                        <UncontrolledDropdown>
                          <DropdownToggle
                            color="link"
                            size="sm"
                            disabled={isUpdatingStatus}
                            style={{
                              width: '30px',
                              height: '30px',
                              padding: '0',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: '#64748b',
                              border: '1px solid #e2e7ed',
                              borderRadius: '5px',
                              textDecoration: 'none',
                            }}
                          >
                            <FaEllipsisV size={11} />
                          </DropdownToggle>

                          <DropdownMenu end>
                            <DropdownItem
                              onClick={() =>
                                updateStatus(
                                  alert.id,
                                  'En seguimiento'
                                )
                              }
                            >
                              Marcar en seguimiento
                            </DropdownItem>

                            <DropdownItem
                              onClick={() =>
                                updateStatus(
                                  alert.id,
                                  'Resuelta'
                                )
                              }
                            >
                              Marcar como resuelta
                            </DropdownItem>

                            <DropdownItem
                              onClick={() =>
                                updateStatus(
                                  alert.id,
                                  'Nueva'
                                )
                              }
                            >
                              Reabrir
                            </DropdownItem>
                          </DropdownMenu>
                        </UncontrolledDropdown>
                      </div>
                    </td>
                  </tr>
                ))}

              {!isLoading && !alerts.length && (
                <tr>
                  <td
                    colSpan="6"
                    style={{
                      textAlign: 'center',
                      color: '#64748b',
                      padding: '35px',
                      fontSize: '12px',
                    }}
                  >
                    No hay alertas que coincidan con los filtros.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: '14px',
            flexWrap: 'wrap',
            gap: '10px',
          }}
        >
          <span
            style={{
              color: '#64748b',
              fontSize: '10px',
            }}
          >
            Mostrando {alerts.length} de {pagination.total} alertas
          </span>

          <PaginationRange
            currentPage={currentPage}
            totalPages={totalPages}
            setCurrentPage={setCurrentPage}
          />
        </div>
      </CardBody>
    </Card>
  );
};

export default AlertTable;